/**
 * The org-chart arithmetic: apply a scenario to a baseline, walk lines, lay
 * out a tree. No React here, so it can be reasoned about on its own.
 *
 * The directory has a quirk every function below respects: the CEO's manager
 * line points at himself. That is the top of the tree, not a cycle.
 */

import type { OrgChange, OrgPerson } from "@/lib/api";

export type OrgMap = Map<string, OrgPerson>;
export type ChangeIndex = Map<string, Map<string, OrgChange>>;

export const FIELD_LABEL: Record<string, string> = {
  manager: "Manager",
  job_title: "Job title",
  department: "Department",
  company_name: "Company",
  office_location: "Office (L2 Area)",
  business_category: "Business category (L2 Area)",
  country: "Country",
  city: "City",
};

export function toMap(people: OrgPerson[]): OrgMap {
  return new Map(people.map((p) => [p.id, p]));
}

export function indexChanges(changes: OrgChange[]): ChangeIndex {
  const out: ChangeIndex = new Map();
  for (const c of changes) {
    const m = out.get(c.subject_id) ?? new Map<string, OrgChange>();
    m.set(c.field, c);
    out.set(c.subject_id, m);
  }
  return out;
}

/** The baseline with every change applied. */
export function project(before: OrgMap, changes: OrgChange[]): OrgMap {
  const after: OrgMap = new Map();
  for (const [id, p] of before) {
    after.set(id, { ...p });
  }
  for (const c of changes) {
    const p = after.get(c.subject_id);
    if (!p) {
      continue;
    }
    if (c.field === "manager") {
      p.manager_id = c.new ?? null;
    } else {
      (p as unknown as Record<string, unknown>)[c.field] = c.new;
    }
  }
  return after;
}

function parentOf(map: OrgMap, id: string): string | null {
  const m = map.get(id)?.manager_id;
  return m && m !== id && map.has(m) ? m : null;
}

/** From `id` up to the top, stopping on a loop. */
export function pathToTop(map: OrgMap, id: string): string[] {
  const path: string[] = [];
  let cur: string | null = id;
  while (cur && !path.includes(cur)) {
    path.push(cur);
    cur = parentOf(map, cur);
  }
  return path;
}

/** The lowest person every one of `ids` sits under (or is). */
export function commonAncestor(map: OrgMap, ids: string[]): string | null {
  const paths = ids.filter((id) => map.has(id)).map((id) => pathToTop(map, id));
  if (paths.length === 0) {
    return null;
  }
  const shared = paths
    .slice(1)
    .reduce<string[]>((acc, path) => acc.filter((x) => path.includes(x)), paths[0] ?? []);
  return shared[0] ?? null;
}

/**
 * The top of the org. The CEO's manager line points at himself, but so does
 * at least one other account (Abel Vasquez, measured 30 Sep 2026), so "is his
 * own manager" alone picks whoever comes first. The real top is the one of
 * those with the most people reporting to them.
 */
export function topOf(map: OrgMap): string | null {
  const reports = new Map<string, number>();
  for (const p of map.values()) {
    if (p.manager_id && p.manager_id !== p.id && p.account_enabled !== false) {
      reports.set(p.manager_id, (reports.get(p.manager_id) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  for (const p of map.values()) {
    if (p.manager_id === p.id && p.account_enabled !== false) {
      if (best === null || (reports.get(p.id) ?? 0) > (reports.get(best) ?? 0)) {
        best = p.id;
      }
    }
  }
  return best;
}

export function childrenIndex(
  map: OrgMap,
  include: (p: OrgPerson) => boolean,
): Map<string, OrgPerson[]> {
  const out = new Map<string, OrgPerson[]>();
  for (const p of map.values()) {
    if (!include(p)) {
      continue;
    }
    const parent = parentOf(map, p.id);
    if (!parent) {
      continue;
    }
    const list = out.get(parent) ?? [];
    list.push(p);
    out.set(parent, list);
  }
  for (const list of out.values()) {
    list.sort((a, b) => (a.display_name ?? "").localeCompare(b.display_name ?? ""));
  }
  return out;
}

export interface LaidNode {
  id: string;
  x: number;
  y: number;
  depth: number;
  childCount: number;
  collapsed: boolean;
}

export interface Layout {
  nodes: LaidNode[];
  links: { from: string; to: string }[];
  width: number;
  height: number;
}

export const NODE_W = 232;
export const NODE_H = 46;
const COL_W = NODE_W + 56;
const ROW_H = NODE_H + 12;

/**
 * Left-to-right tidy tree: one row per visible leaf, a parent centred on its
 * children. Wide spans become tall, not wide, which is what a screen can take
 * (Timo's 30 mailboxes, a manager with 55 reports).
 */
export function layout(
  rootId: string,
  children: Map<string, OrgPerson[]>,
  collapsed: Set<string>,
): Layout {
  const nodes: LaidNode[] = [];
  const links: { from: string; to: string }[] = [];
  let row = 0;
  let maxDepth = 0;
  const seen = new Set<string>();

  const place = (id: string, depth: number): number => {
    seen.add(id);
    maxDepth = Math.max(maxDepth, depth);
    const kids = (children.get(id) ?? []).filter((k) => !seen.has(k.id));
    const isCollapsed = collapsed.has(id) && kids.length > 0;
    let y: number;
    if (kids.length === 0 || isCollapsed) {
      y = row++ * ROW_H;
    } else {
      const ys = kids.map((k) => {
        links.push({ from: id, to: k.id });
        return place(k.id, depth + 1);
      });
      y = ((ys[0] ?? 0) + (ys[ys.length - 1] ?? 0)) / 2;
    }
    nodes.push({ id, x: depth * COL_W, y, depth, childCount: kids.length, collapsed: isCollapsed });
    return y;
  };
  place(rootId, 0);
  return {
    nodes,
    links,
    width: maxDepth * COL_W + NODE_W + 8,
    height: Math.max(row, 1) * ROW_H,
  };
}
